/**
 * Automatic reserve transfers (Fan, 2026-09-27).
 *
 * Each paid invoice's VAT, corporation tax and profit share are moved from
 * the main Wise balance into Wise vaults by a scheduled job. This card shows
 * what will move next, what already moved, and the two switches Fan needs:
 * turn it on (after checking the first amounts) and HMRC collection dates.
 */
import { useState, useEffect, useCallback } from 'react';
import { Loader2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { brosReserveTransfersAPI } from '../lib/api';
import { formatCurrency } from '../lib/chartFormatters';
import { getErrorMessage } from '../lib/errors';

const STATUS_TEXT = {
  moved: 'Moved',
  insufficient_funds: 'Not enough in the main balance yet',
  no_main_balance: 'No main balance in this currency',
  below_minimum: 'Too small, waits for the next payment',
  failed: 'Failed',
};

const CT_PAID_TEXT = {
  moved: 'Taken from the Corporate Tax vault',
  vault_empty: 'Corporate Tax vault empty, nothing moved',
  no_vault: 'No Corporate Tax vault',
  prefunded: 'Already pre-funded',
  no_rate: 'No exchange rate yet',
  failed: 'Failed',
};

const BUCKET_TEXT = { vat_reserve: 'VAT', corp_tax_reserve: 'Corporation tax', overheads: 'Overheads', profit: 'Profit' };

const RELEASE_TEXT = {
  moved: 'Taken from Overheads',
  vault_short: 'Not enough in Overheads yet',
  no_rate: 'No exchange rate',
  failed: 'Failed',
};

const VAT_PAID_TEXT = {
  moved: 'Taken from the VAT vault',
  vault_empty: 'VAT vault empty, nothing moved',
  no_vault: 'No VAT vault in this currency',
  prefunded: 'Already pre-funded',
  no_main_balance: 'No main balance in this currency',
  failed: 'Failed',
};

export function BROSReserveTransfers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [hmrc, setHmrc] = useState({ date: '', amount: '', bucket: 'vat_reserve' });
  const [targets, setTargets] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await brosReserveTransfersAPI.getStatus();
      setData(res.data);
      setTargets({
        monthly: res.data.config.overheads_monthly_target_gbp || '',
        buffer: res.data.config.buffer_target_gbp || '',
      });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setMode = async (mode) => {
    setBusy(true);
    try {
      await brosReserveTransfersAPI.updateConfig({ mode });
      toast.success(mode === 'live' ? 'Automatic transfers are on' : 'Automatic transfers paused');
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const saveOverheads = async (patch, message) => {
    setBusy(true);
    try {
      await brosReserveTransfersAPI.updateConfig(patch);
      toast.success(message);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await brosReserveTransfersAPI.run({ live: true });
      if (res.data.halted) toast.error(res.data.halted);
      else toast.success('Transfers run');
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const addHmrc = async () => {
    setBusy(true);
    try {
      await brosReserveTransfersAPI.addHmrcCollection({ ...hmrc, currency: 'GBP' });
      setHmrc({ date: '', amount: '', bucket: 'vat_reserve' });
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const removeHmrc = async (id) => {
    setBusy(true);
    try {
      await brosReserveTransfersAPI.removeHmrcCollection(id);
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) {
    return (
      <Card><CardContent className="py-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading transfers
      </CardContent></Card>
    );
  }
  if (!data) return null;

  const { config, plan, moved, last_run: lastRun, wise_token_ok: tokenOk } = data;
  const live = config.mode === 'live';
  const waterfall = config.profit_mode === 'waterfall' && config.waterfall_start_date;
  const thisMonth = new Date().toISOString().slice(0, 7);
  const monthFill = plan.waterfall?.months?.[thisMonth];
  const releases = plan.overhead_releases || [];
  const lastReleases = {};
  (lastRun?.releases || []).forEach((r) => { lastReleases[r.tx_id] = r; });
  const vatPaid = plan.vat_paid_releases || [];
  const lastVatPaid = {};
  (lastRun?.vat_paid || []).forEach((r) => { lastVatPaid[r.tx_id] = r; });
  const ctPaid = plan.corp_tax_paid_releases || [];
  const lastCtPaid = {};
  (lastRun?.corp_tax_paid || []).forEach((r) => { lastCtPaid[r.tx_id] = r; });
  const lastResults = {};
  (lastRun?.results || []).forEach((r) => { lastResults[`${r.currency}:${r.bucket}`] = r; });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Automatic reserve transfers</CardTitle>
            <CardDescription>
              {waterfall ? (
                <>VAT and corporation tax move to their vaults. Profit after tax fills Overheads first, then
                the buffer, then Profit (payments from {config.waterfall_start_date}).</>
              ) : (
                <>VAT, corporation tax and {config.profit_share_pct}% of profit after tax move from the main
                Wise balance into Wise vaults, for payments from {config.start_date}.</>
              )}
            </CardDescription>
          </div>
          <Badge variant={live ? 'default' : 'secondary'}>{live ? 'On' : 'Preview only'}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        {!tokenOk && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-amber-900">
            <Info className="h-4 w-4 mt-0.5" /> Wise is not connected, so nothing can move yet.
          </div>
        )}

        <div>
          <div className="font-medium mb-2">Next to move</div>
          {plan.movements.length === 0 ? (
            <div className="text-muted-foreground">Nothing waiting.</div>
          ) : (
            <div className="space-y-1">
              {plan.movements.map((m) => {
                const last = lastResults[`${m.currency}:${m.bucket}`];
                return (
                  <div key={`${m.currency}-${m.bucket}`} className="flex items-center justify-between border-b py-1">
                    <span>{BUCKET_TEXT[m.bucket]} ({m.currency}), {m.items.length} payment{m.items.length === 1 ? '' : 's'}</span>
                    <span className="flex items-center gap-3">
                      {last && last.status !== 'moved' && (
                        <span className="text-xs text-muted-foreground" title={last.error || ''}>
                          {STATUS_TEXT[last.status] || last.status}
                        </span>
                      )}
                      <span className="tabular-nums font-medium">{formatCurrency(Number(m.amount), m.currency)}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {plan.waiting_supplier_cost.length > 0 && (
            <div className="mt-2 text-xs text-muted-foreground"
                 title={plan.waiting_supplier_cost.join(', ')}>
              Profit share waits for supplier cost on {plan.waiting_supplier_cost.length} payment(s).
            </div>
          )}
        </div>

        <div>
          <div className="font-medium mb-2">Moved so far</div>
          {Object.keys(moved).length === 0 ? (
            <div className="text-muted-foreground">Nothing yet.</div>
          ) : Object.entries(moved).map(([cur, buckets]) => (
            <div key={cur} className="flex flex-wrap gap-4">
              {Object.entries(buckets).map(([b, amt]) => (
                <span key={b}>{BUCKET_TEXT[b]}: <span className="tabular-nums">{formatCurrency(Number(amt), cur)}</span></span>
              ))}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {live ? (
            <>
              <Button size="sm" onClick={runNow} disabled={busy || !tokenOk}>Run now</Button>
              <Button size="sm" variant="outline" onClick={() => setMode('preview')} disabled={busy}>Pause</Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setMode('live')} disabled={busy || !tokenOk}>
              Amounts look right, turn on
            </Button>
          )}
        </div>


        <div>
          <div className="font-medium mb-1">Overheads first</div>
          <div className="text-xs text-muted-foreground mb-2">
            Each month, profit after tax first fills the Overheads vault up to the monthly target. Then it builds
            the buffer, which stays in the main balance. Everything after that goes to Profit. When salaries or
            subscriptions are paid from Wise, the same amount comes out of the Overheads vault.
          </div>
          {targets && (
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span>Monthly overheads £</span>
              <Input type="number" className="w-28" value={targets.monthly}
                     onChange={(e) => setTargets({ ...targets, monthly: e.target.value })} />
              <span>Buffer £</span>
              <Input type="number" className="w-28" value={targets.buffer}
                     onChange={(e) => setTargets({ ...targets, buffer: e.target.value })} />
              <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => saveOverheads({ overheads_monthly_target_gbp: targets.monthly,
                        buffer_target_gbp: targets.buffer }, 'Targets saved')}>Save</Button>
            </div>
          )}
          {waterfall ? (
            <div className="space-y-1">
              <div className="flex justify-between border-b py-1">
                <span>Overheads filled this month</span>
                <span className="tabular-nums">
                  {formatCurrency(Number(monthFill?.overheads_gbp || 0), 'GBP')} of {formatCurrency(Number(config.overheads_monthly_target_gbp), 'GBP')}
                </span>
              </div>
              <div className="flex justify-between border-b py-1">
                <span>Buffer kept in main</span>
                <span className="tabular-nums">
                  {formatCurrency(Number(plan.waterfall?.buffer_kept_gbp || 0), 'GBP')} of {formatCurrency(Number(config.buffer_target_gbp), 'GBP')}
                </span>
              </div>
              <div className="font-medium mt-3 mb-1">Overheads paid, to take from the vault</div>
              {releases.length === 0 ? (
                <div className="text-muted-foreground">Nothing waiting.</div>
              ) : releases.map((r) => {
                const last = lastReleases[r.tx_id];
                return (
                  <div key={r.tx_id} className="flex items-center justify-between border-b py-1">
                    <span>{r.date} {r.label} ({r.counterparty})</span>
                    <span className="flex items-center gap-3">
                      {last && <span className="text-xs text-muted-foreground">{RELEASE_TEXT[last.status] || last.status}</span>}
                      <span className="tabular-nums">{formatCurrency(Number(r.amount), r.currency)}</span>
                    </span>
                  </div>
                );
              })}
              <Button size="sm" variant="ghost" className="mt-2" disabled={busy}
                      onClick={() => saveOverheads({ profit_mode: 'split' }, 'Back to the profit split')}>
                Go back to {config.profit_share_pct}% profit split
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => saveOverheads({ profit_mode: 'waterfall' }, 'Overheads first is on from today')}>
              Switch to overheads first (from today)
            </Button>
          )}
        </div>

        <div>
          <div className="font-medium mb-1">VAT paid</div>
          <div className="text-xs text-muted-foreground mb-2">
            When VAT is paid to HMRC, the Finanzamt or the Revenue Commissioners, the same amount comes out of
            the VAT vault of that currency, never more than the vault holds. VAT refunds never move money.
          </div>
          {config.vat_paid_start_date ? (
            <div className="space-y-1">
              <div className="text-muted-foreground">On for VAT paid from {config.vat_paid_start_date}.</div>
              {vatPaid.length === 0 ? (
                <div className="text-muted-foreground">Nothing waiting.</div>
              ) : vatPaid.map((r) => {
                const last = lastVatPaid[r.tx_id];
                return (
                  <div key={r.tx_id} className="flex items-center justify-between border-b py-1">
                    <span>{r.date} VAT paid: {r.label}</span>
                    <span className="flex items-center gap-3">
                      {last && <span className="text-xs text-muted-foreground">{VAT_PAID_TEXT[last.status] || last.status}</span>}
                      <span className="tabular-nums">{formatCurrency(Number(r.amount), r.currency)}</span>
                    </span>
                  </div>
                );
              })}
              <Button size="sm" variant="ghost" className="mt-2" disabled={busy}
                      onClick={() => saveOverheads({ vat_paid_start_date: null }, 'VAT paid release is off')}>
                Turn off
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => saveOverheads({ vat_paid_start_date: new Date().toISOString().slice(0, 10) },
                      'VAT paid release is on from today')}>
              Take VAT paid out of the VAT vault (from today)
            </Button>
          )}
        </div>

        <div>
          <div className="font-medium mb-1">Corporation tax paid</div>
          <div className="text-xs text-muted-foreground mb-2">
            When corporation tax is paid to HMRC, the same value comes out of the Corporate Tax vaults: the
            payment currency first, then the others at that day's rate, never more than they hold.
          </div>
          {config.corp_tax_paid_start_date ? (
            <div className="space-y-1">
              <div className="text-muted-foreground">On for corporation tax paid from {config.corp_tax_paid_start_date}.</div>
              {ctPaid.length === 0 ? (
                <div className="text-muted-foreground">Nothing waiting.</div>
              ) : ctPaid.map((r) => {
                const last = lastCtPaid[r.tx_id];
                return (
                  <div key={r.tx_id} className="flex items-center justify-between border-b py-1">
                    <span>{r.date} Corporation tax paid: {r.label}</span>
                    <span className="flex items-center gap-3">
                      {last && <span className="text-xs text-muted-foreground">{CT_PAID_TEXT[last.status] || last.status}</span>}
                      <span className="tabular-nums">{formatCurrency(Number(r.amount), r.currency)}</span>
                    </span>
                  </div>
                );
              })}
              <Button size="sm" variant="ghost" className="mt-2" disabled={busy}
                      onClick={() => saveOverheads({ corp_tax_paid_start_date: null }, 'Corporation tax paid release is off')}>
                Turn off
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => saveOverheads({ corp_tax_paid_start_date: new Date().toISOString().slice(0, 10) },
                      'Corporation tax paid release is on from today')}>
              Take corporation tax paid out of the Corporate Tax vault (from today)
            </Button>
          )}
        </div>

        <div>
          <div className="font-medium mb-1">HMRC collections</div>
          <div className="text-xs text-muted-foreground mb-2">
            {config.hmrc_prefund_days} days before each date, the amount moves from the tax vault back to the
            main GBP balance so the Direct Debit can take it.
          </div>
          {(config.hmrc_collections || []).map((c) => (
            <div key={c.id} className="flex items-center justify-between border-b py-1">
              <span>{c.date} {BUCKET_TEXT[c.bucket]}</span>
              <span className="flex items-center gap-3">
                <span className="tabular-nums">{formatCurrency(Number(c.amount), c.currency)}</span>
                {c.status === 'scheduled' ? (
                  <Button size="sm" variant="ghost" onClick={() => removeHmrc(c.id)} disabled={busy}>Remove</Button>
                ) : <Badge variant="secondary">Funded</Badge>}
              </span>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Input type="date" className="w-40" value={hmrc.date}
                   onChange={(e) => setHmrc({ ...hmrc, date: e.target.value })} />
            <Input type="number" className="w-32" placeholder="Amount" value={hmrc.amount}
                   onChange={(e) => setHmrc({ ...hmrc, amount: e.target.value })} />
            <select className="h-9 rounded-md border px-2" value={hmrc.bucket}
                    onChange={(e) => setHmrc({ ...hmrc, bucket: e.target.value })}>
              <option value="vat_reserve">VAT</option>
              <option value="corp_tax_reserve">Corporation tax</option>
            </select>
            <Button size="sm" variant="outline" onClick={addHmrc}
                    disabled={busy || !hmrc.date || !hmrc.amount}>Add</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
